import * as React from 'react';
import { useNavigate } from 'react-router';
import {
  PlusIcon,
  SearchIcon,
  Trash2Icon,
  UploadIcon,
  PencilIcon,
  MapPinIcon,
  HelpCircleIcon,
} from 'lucide-react';
import {
  useGetWorkspaceAssets,
  useCreateWorkspaceAsset,
  useUpdateWorkspaceAsset,
  useRetireWorkspaceAsset,
  useBulkRetireWorkspaceAssets,
  useGetWorkspaceAssetTypes,
  useGetWorkspaceRegions,
  getGetWorkspaceAssetsQueryKey,
} from '@cdf/api-client-react';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { MapPinPicker } from '@/components/map/map-pin-picker';
import {
  Dialog as HelpDialog,
  DialogContent as HelpDialogContent,
  DialogHeader as HelpDialogHeader,
  DialogTitle as HelpDialogTitle,
} from '@/components/ui/dialog';

const conditionColors: Record<string, string> = {
  good: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  fair: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  poor: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  damaged: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export function AssetRegisterPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = React.useState('');
  const [assetTypeFilter, setAssetTypeFilter] = React.useState<string>('all');
  const [conditionFilter, setConditionFilter] = React.useState<string>('all');
  const [includeRetired, setIncludeRetired] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());

  // Create/edit dialog state
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingAsset, setEditingAsset] = React.useState<
    | {
        id: string;
        assetTypeId: string;
        latitude: number;
        longitude: number;
        address: string | null;
        condition: string;
        regionId: string | null;
        externalSource: string | null;
        externalId: string | null;
      }
    | undefined
  >(undefined);

  // Help dialog
  const [helpOpen, setHelpOpen] = React.useState(false);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const queryParams = {
    search: debouncedSearch || undefined,
    assetTypeId: assetTypeFilter !== 'all' ? assetTypeFilter : undefined,
    condition: conditionFilter !== 'all' ? (conditionFilter as 'good' | 'fair' | 'poor' | 'damaged') : undefined,
    includeRetired: includeRetired ? ('true' as const) : ('false' as const),
    page,
    limit: 50,
  };

  const { data, isLoading } = useGetWorkspaceAssets(queryParams);
  const { data: assetTypesData } = useGetWorkspaceAssetTypes();
  const { data: regionsData } = useGetWorkspaceRegions();

  const createMutation = useCreateWorkspaceAsset();
  const updateMutation = useUpdateWorkspaceAsset();
  const retireMutation = useRetireWorkspaceAsset();
  const bulkRetireMutation = useBulkRetireWorkspaceAssets();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetWorkspaceAssetsQueryKey() });
  };

  const assetTypes = assetTypesData?.assetTypes ?? [];
  const regions = regionsData?.regions ?? [];
  const assetsList = data?.assets ?? [];
  const total = data?.total ?? 0;

  const handleOpenCreate = () => {
    setEditingAsset(undefined);
    setDialogOpen(true);
  };

  const handleOpenEdit = (asset: (typeof assetsList)[0]) => {
    setEditingAsset({
      id: asset.id ?? '',
      assetTypeId: asset.assetTypeId ?? '',
      latitude: asset.latitude ?? 0,
      longitude: asset.longitude ?? 0,
      address: asset.address ?? null,
      condition: asset.condition ?? 'good',
      regionId: asset.regionId ?? null,
      externalSource: asset.externalSource ?? null,
      externalId: asset.externalId ?? null,
    });
    setDialogOpen(true);
  };

  const handleRetire = async (assetId: string) => {
    await retireMutation.mutateAsync({ assetId });
    invalidate();
  };

  const handleBulkRetire = async () => {
    if (selectedIds.size === 0) return;
    await bulkRetireMutation.mutateAsync({
      data: { assetIds: Array.from(selectedIds) },
    });
    setSelectedIds(new Set());
    invalidate();
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Asset Register"
        icon={MapPinIcon}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setHelpOpen(true)}>
              <HelpCircleIcon className="mr-2 h-4 w-4" />
              Import Contract
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/workspace/assets/import')}>
              <UploadIcon className="mr-2 h-4 w-4" />
              Import
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/workspace/boundaries')}>
              <PencilIcon className="mr-2 h-4 w-4" />
              Boundaries
            </Button>
            <Button size="sm" onClick={handleOpenCreate}>
              <PlusIcon className="mr-2 h-4 w-4" />
              Add Asset
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by address or external ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={assetTypeFilter} onValueChange={setAssetTypeFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Asset type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {assetTypes.map((t) => (
              <SelectItem key={t.id ?? ''} value={t.id ?? ''}>
                {t.name ?? ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={conditionFilter} onValueChange={setConditionFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Condition" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All conditions</SelectItem>
            <SelectItem value="good">Good</SelectItem>
            <SelectItem value="fair">Fair</SelectItem>
            <SelectItem value="poor">Poor</SelectItem>
            <SelectItem value="damaged">Damaged</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={includeRetired}
            onCheckedChange={(v) => setIncludeRetired(v === true)}
          />
          Show retired
        </label>
        {selectedIds.size > 0 && (
          <Button variant="destructive" size="sm" onClick={handleBulkRetire}>
            <Trash2Icon className="mr-2 h-4 w-4" />
            Retire ({selectedIds.size})
          </Button>
        )}
      </div>

      {/* Asset table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]"></TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Condition</TableHead>
                <TableHead>Coordinates</TableHead>
                <TableHead>External ID</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : assetsList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">
                    No assets found.
                  </TableCell>
                </TableRow>
              ) : (
                assetsList.map((asset) => (
                  <TableRow key={asset.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(asset.id ?? '')}
                        onCheckedChange={() => toggleSelect(asset.id ?? '')}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span>{asset.typeName}</span>
                        {asset.retiredAt && (
                          <Badge variant="secondary" className="text-xs">
                            Retired
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{asset.address ?? '—'}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${
                          conditionColors[asset.condition ?? ''] ?? ''
                        }`}
                      >
                        {asset.condition}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {(asset.latitude ?? 0).toFixed(5)}, {(asset.longitude ?? 0).toFixed(5)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {asset.externalSource ? `${asset.externalSource}:` : ''}
                      {asset.externalId ?? '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEdit(asset)}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        {!asset.retiredAt && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRetire(asset.id ?? '')}
                          >
                            <Trash2Icon className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > 50 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * 50 + 1}–{Math.min(page * 50, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page * 50 >= total}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <AssetDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingAsset={editingAsset}
        assetTypes={assetTypes}
        regions={regions}
        onSubmit={async (values) => {
          if (editingAsset) {
            await updateMutation.mutateAsync({
              assetId: editingAsset.id,
              data: {
                assetTypeId: values.assetTypeId,
                latitude: values.latitude,
                longitude: values.longitude,
                address: values.address,
                condition: values.condition as 'good' | 'fair' | 'poor' | 'damaged',
                regionId: values.regionId ?? null,
              },
            });
          } else {
            await createMutation.mutateAsync({
              data: {
                assetTypeId: values.assetTypeId,
                latitude: values.latitude,
                longitude: values.longitude,
                address: values.address,
                condition: values.condition as 'good' | 'fair' | 'poor' | 'damaged',
                regionId: values.regionId,
                externalSource: values.externalSource,
                externalId: values.externalId,
              },
            });
          }
          setDialogOpen(false);
          invalidate();
        }}
      />

      {/* Import Contract Help Dialog */}
      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen}>
        <HelpDialogContent className="max-w-2xl">
          <HelpDialogHeader>
            <HelpDialogTitle>Import Contract</HelpDialogTitle>
          </HelpDialogHeader>
          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-semibold mb-1">Supported Formats</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>GeoJSON</strong> — FeatureCollection where each Feature has Point geometry and properties for asset fields.</li>
                <li><strong>CSV</strong> — Header row + data rows. At minimum requires latitude and longitude columns.</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Expected Fields</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li><code>latitude</code> / <code>lat</code> — WGS84 decimal degrees (-90 to 90)</li>
                <li><code>longitude</code> / <code>lng</code> / <code>lon</code> — WGS84 decimal degrees (-180 to 180)</li>
                <li><code>asset_type</code> or <code>asset_type_id</code> — matches seeded taxonomy by name or slug</li>
                <li><code>condition</code> — one of: good, fair, poor, damaged (defaults to good)</li>
                <li><code>address</code> — human-readable location description (optional)</li>
                <li><code>external_source</code> — data source identifier (e.g. "council-gis-2024")</li>
                <li><code>external_id</code> — unique ID within the source for idempotent upsert</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Coordinate System</h4>
              <p>All coordinates must be <strong>WGS84 (EPSG:4326)</strong> — the same system used by GPS and web maps.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Upsert Behavior</h4>
              <p>Re-importing a file with the same <code>external_source</code> + <code>external_id</code> updates the existing asset instead of creating a duplicate. This enables scheduled sync from GIS feeds without schema changes.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Import Flow</h4>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Upload file → system parses and auto-suggests column mapping</li>
                <li>Review/adjust mapping → validate → see per-row errors</li>
                <li>Commit → assets are created or updated in a transaction</li>
                <li>Import history records counts and downloadable error reports</li>
              </ol>
            </div>
          </div>
        </HelpDialogContent>
      </HelpDialog>
    </div>
  );
}

// ── Asset Create/Edit Dialog ──────────────────────────────────────────────

interface AssetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingAsset?: {
    id: string;
    assetTypeId: string;
    latitude: number;
    longitude: number;
    address: string | null;
    condition: string;
    regionId: string | null;
    externalSource: string | null;
    externalId: string | null;
  };
  assetTypes: Array<{ id?: string; name?: string; slug?: string; icon?: string }>;
  regions: Array<{ id?: string; name?: string }>;
  onSubmit: (values: {
    assetTypeId: string;
    latitude: number;
    longitude: number;
    address?: string;
    condition: string;
    regionId?: string;
    externalSource?: string;
    externalId?: string;
  }) => Promise<void>;
}

function AssetDialog({
  open,
  onOpenChange,
  editingAsset,
  assetTypes,
  regions,
  onSubmit,
}: AssetDialogProps) {
  const [assetTypeId, setAssetTypeId] = React.useState('');
  const [latitude, setLatitude] = React.useState(-37.8136);
  const [longitude, setLongitude] = React.useState(144.9631);
  const [address, setAddress] = React.useState('');
  const [condition, setCondition] = React.useState('good');
  const [regionId, setRegionId] = React.useState<string>('none');
  const [externalSource, setExternalSource] = React.useState('');
  const [externalId, setExternalId] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (editingAsset) {
      setAssetTypeId(editingAsset.assetTypeId);
      setLatitude(editingAsset.latitude);
      setLongitude(editingAsset.longitude);
      setAddress(editingAsset.address ?? '');
      setCondition(editingAsset.condition);
      setRegionId(editingAsset.regionId ?? 'none');
      setExternalSource(editingAsset.externalSource ?? '');
      setExternalId(editingAsset.externalId ?? '');
    } else {
      setAssetTypeId('');
      setLatitude(-37.8136);
      setLongitude(144.9631);
      setAddress('');
      setCondition('good');
      setRegionId('none');
      setExternalSource('');
      setExternalId('');
    }
  }, [editingAsset, open]);

  const handleSubmit = async () => {
    if (!assetTypeId) return;
    setSaving(true);
    try {
      await onSubmit({
        assetTypeId,
        latitude,
        longitude,
        address: address || undefined,
        condition,
        regionId: regionId !== 'none' ? regionId : undefined,
        externalSource: externalSource || undefined,
        externalId: externalId || undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {editingAsset ? 'Edit Asset' : 'Create Asset'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Map placement */}
          <div>
            <Label>Location — click to place or drag the marker</Label>
            <div className="mt-2 overflow-hidden rounded-lg border">
              <MapPinPicker
                latitude={latitude}
                longitude={longitude}
                onLocationChange={(lat, lng) => {
                  setLatitude(lat);
                  setLongitude(lng);
                }}
              />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {latitude.toFixed(5)}, {longitude.toFixed(5)}
            </p>
          </div>

          {/* Asset type */}
          <div>
            <Label>Asset Type</Label>
            <Select value={assetTypeId} onValueChange={setAssetTypeId}>
              <SelectTrigger className="mt-1 w-full">
                <SelectValue placeholder="Select asset type" />
              </SelectTrigger>
              <SelectContent>
                {assetTypes.map((t) => (
                  <SelectItem key={t.id ?? ''} value={t.id ?? ''}>
                    {t.name ?? ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Condition + Region */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Condition</Label>
              <Select value={condition} onValueChange={setCondition}>
                <SelectTrigger className="mt-1 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="fair">Fair</SelectItem>
                  <SelectItem value="poor">Poor</SelectItem>
                  <SelectItem value="damaged">Damaged</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Region</Label>
              <Select value={regionId} onValueChange={setRegionId}>
                <SelectTrigger className="mt-1 w-full">
                  <SelectValue placeholder="No region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No region</SelectItem>
                  {regions.map((r) => (
                    <SelectItem key={r.id ?? ''} value={r.id ?? ''}>
                      {r.name ?? ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Address */}
          <div>
            <Label>Address (optional)</Label>
            <Input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="123 Main St, Suburb"
              className="mt-1"
            />
          </div>

          {/* External source/ID */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>External Source (optional)</Label>
              <Input
                value={externalSource}
                onChange={(e) => setExternalSource(e.target.value)}
                placeholder="council-gis-2024"
                className="mt-1"
              />
            </div>
            <div>
              <Label>External ID (optional)</Label>
              <Input
                value={externalId}
                onChange={(e) => setExternalId(e.target.value)}
                placeholder="SIGN-00123"
                className="mt-1"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !assetTypeId}>
            {saving ? 'Saving...' : editingAsset ? 'Save Changes' : 'Create Asset'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
